// ✅ Trekkin.app — HUDCard.jsx v1.1.0 (DEBUG ANDROID)
// -------------------------------------------------------------
// 🔹 Lê dados brutos do usePanelsStore (userPosition, accuracy, lastSpeed)
// 🔹 Mostra velocidade RAW e velocidade FILTRADA
// 🔹 Filtro anti-fantasma para Android parado:
//      • Guarda historial curto de posições
//      • Se em ~5s andou < 3 m → considera parado (vel = 0)
// 🔹 NÃO altera nada na gravação nem no MapView (só visual)
// -------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from "react";
import { usePanelsStore } from "../stores/usePanelsStore";
import { useTranslate } from "../i18n/useTranslate";

// Haversine simples em metros
const haversine = (a, b) => {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export default function HUDCard() {
  const translate = useTranslate();
  const pos = usePanelsStore((s) => s.userPosition);
  const acc = usePanelsStore((s) => s.accuracyNow);
  const rawSpeed = usePanelsStore((s) => s.lastSpeed || 0); // km/h
  const ele = usePanelsStore((s) => s.lastElevation || 0);

  // Histórico curto de posições (apenas para filtro de velocidade)
  const [history, setHistory] = useState([]);
  const lastTsRef = useRef(null);

  useEffect(() => {
    if (!pos || typeof pos.lat !== "number" || typeof pos.lon !== "number") {
      return;
    }

    const now = Date.now();
    const entry = {
      lat: pos.lat,
      lon: pos.lon,
      ts: now,
    };

    setHistory((prev) => {
      const arr = [...prev, entry];
      // manter ~10s de histórico
      const cutoff = now - 10000;
      return arr.filter((p) => p.ts >= cutoff);
    });

    lastTsRef.current = now;
  }, [pos?.lat, pos?.lon]);

  // Velocidade filtrada com base no histórico
  const filteredSpeed = useMemo(() => {
    if (!history.length) return 0;

    const sorted = [...history].sort((a, b) => a.ts - b.ts);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const dt = (last.ts - first.ts) / 1000;

    // Pouco tempo → confiar mais no valor raw
    if (!dt || dt < 2) {
      return Math.max(0, rawSpeed);
    }

    // Distância total percorrida no intervalo
    let dist = 0;
    for (let i = 1; i < sorted.length; i++) {
      dist += haversine(sorted[i - 1], sorted[i]);
    }

    // Se em >4s andou menos de 3 m → parado (velocidade = 0)
    if (dt > 4 && dist < 3) {
      return 0;
    }

    // Caso contrário, usa velocidade raw mas sem negativos e sem spikes absurdos
    let v = Math.max(0, rawSpeed);

    // Se precisão for fraca, suavizar um bocado
    if (acc && acc > 12 && v < 4) {
      v = 0;
    }

    // Limitar spikes ridículos quando parado
    if (dist < 5 && v > 8) {
      v = 0;
    }

    return v;
  }, [history, rawSpeed, acc]);

  const lat = pos?.lat;
  const lon = pos?.lon;

  return (
    <section className="tk-gps-status-card" aria-label={translate("hud.gpsStatus")}>
      <header className="tk-gps-status-header">
        <span aria-hidden="true" />
        <strong>{translate("hud.gpsStatus")}</strong>
        <b aria-hidden="true">›</b>
      </header>
      <div className="tk-gps-status-grid">
        <div>
          <strong>📍 {lat && lon ? `${lat.toFixed(5)}, ${lon.toFixed(5)}` : "— / —"}</strong>
          <small>{translate("hud.latLon")}</small>
        </div>
        <div>
          <strong>📡 {Number.isFinite(acc) ? `${acc.toFixed(0)} m` : "—"}</strong>
          <small>{translate("hud.accuracy")}</small>
        </div>
        <div>
          <strong>⛰️ {Math.round(ele)} m</strong>
          <small>{translate("hud.altitude")}</small>
        </div>
        <div>
          <strong>🔍 {filteredSpeed.toFixed(1)} km/h</strong>
          <small>{translate("hud.speedFiltered")}</small>
        </div>
      </div>
    </section>
  );
}
